import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      )}
      {...props}
    />
  )
}

/*
 * Tap-target note (design language rule 2, added 2026-08-03).
 *
 * The horizontal list is a fixed `h-10` (40px) and `TabsTrigger` is `h-[calc(100%-1px)]` inside
 * `p-[3px]`, so a trigger renders 40 - 6 - 1 = 33px — 11px under the 44px coarse-pointer floor, on
 * a control that is the primary affordance of the account-access sheet (Log In / Sign Up) and of
 * every add-to-group / add-to-series dialog.
 *
 * `touch-hit-44` is NOT usable here, and the register's high-severity finding recommending it is a
 * wrong diagnosis: `TabsTrigger` already owns its `::after` pseudo-element for the `line` variant's
 * active underline, and the utility's expander is also an `::after`. Applying it would either be
 * overwritten by the underline or silently overwrite it.
 *
 * So the row GROWS on a coarse pointer instead — the sanctioned `min-h-11` floor, gated on the
 * input device and never on a viewport breakpoint. Releasing the list's fixed height to `h-auto` is
 * what lets the trigger's own floor drive it: with a fixed `h-11` the trigger would still resolve to
 * 44 - 6 - 1 = 37px. A mouse keeps the 40px list and the dense 33px trigger exactly as before.
 *
 * Active-state note (2026-08-09). The selected pill used to declare
 * `data-[state=active]:shadow-[var(--surface-shadow-soft)]` as well. That token is deliberately `none`
 * in light, so the declaration was dead there while reading to the next editor as though the active
 * state had an elevation treatment it does not — and it is a real 24px drop-shadow in `.dark`, i.e.
 * rule 1's violation on a resting surface. The selected state is carried by
 * `data-[state=active]:border-border/70` plus `bg-card` and the bolder label; strengthen the border if
 * it ever needs more separation, never add the shadow back. Guard:
 * `flatXRestingSurfaces.contract.test.tsx`.
 */
const tabsListVariants = cva(
  "rounded-xl border border-border/60 p-[3px] group-data-[orientation=horizontal]/tabs:h-10 group-data-[orientation=horizontal]/tabs:[@media(pointer:coarse)]:h-auto data-[variant=line]:rounded-none data-[variant=line]:border-b data-[variant=line]:border-x-0 data-[variant=line]:border-t-0 data-[variant=line]:p-0 group/tabs-list text-muted-foreground inline-flex w-fit items-center justify-center group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col",
  {
    variants: {
      variant: {
        default: "bg-muted/90 shadow-[inset_0_1px_0_rgb(255_255_255_/_0.08)]",
        line: "gap-1 bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-foreground/60 hover:text-foreground relative inline-flex h-[calc(100%-1px)] touch-manipulation [@media(pointer:coarse)]:min-h-11 flex-1 items-center justify-center gap-1.5 rounded-lg border border-transparent px-2.5 py-1.5 text-sm font-medium whitespace-nowrap transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:border-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent",
        "group-data-[variant=default]/tabs-list:data-[state=active]:border-border/70 group-data-[variant=default]/tabs-list:data-[state=active]:bg-card data-[state=active]:text-foreground group-data-[variant=line]/tabs-list:data-[state=active]:text-foreground",
        "after:bg-foreground after:absolute after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
